import { toast } from 'react-toastify';
import { NXForm } from '../../components/common';
import { useEffect, useState } from 'react';
import useApi from '../../api';
import NxtbnDrawer from '../../components/drawer';
import SelectStyled from '../../components/Select';
import { makeEnumFriendly } from '../../enum';
import { useDispatch, useSelector } from 'react-redux';
import { fetchWarehouse } from '../../redux/warehosueSlice';
import { RootState } from '../../redux/rootReducer';
import ProductWithVariantSearch from '../../components/common/productWithVariantSearchField';

import { gql } from "@apollo/client";
import { useQuery } from "@apollo/client";


const GET_STOCK_TRANSFER_DETAILS = gql`
  query GetStockTransferDetails($id: ID!) {
    stockTransfer(id: $id) {
      dbId
      status
      toWarehouse {
        dbId
        name
        location
      }
      fromWarehouse {
        dbId
        name
        location
      }
      items {
        edges {
          node {
            dbId
            id
            quantity
            receivedQuantity
            rejectedQuantity
            variant {
              dbId
              name
              price
            }
          }
        }
      }
    }
  }
`;



interface StackTransferFormProps {
    isOpen: boolean;
    onClose: () => void;
    refresh: () => void;
    id?: number;
}

function StackTransferForm({ isOpen, onClose, refresh, id }: StackTransferFormProps) {
    const [formData, setFormData] = useState<any>({ items: [{}] });
    const [errorData, setErrorData] = useState<any>({});
    const [items, setItems] = useState<any>([]); // duplicate state to pass to productWithVariantSearchField to avoid infinite loop of re-rendering/useEffect
    const warehouses = useSelector((state: RootState) => state.warehouse);

    const { data, loading, error, refetch } = useQuery(GET_STOCK_TRANSFER_DETAILS, {
        variables: { id: id },
            notifyOnNetworkStatusChange: true,
    });

    const api = useApi();
    const dispatch = useDispatch();

    const getWarehouses = () => {
        dispatch(fetchWarehouse() as any);
    };

    useEffect(() => {
        getWarehouses();
    }, []);


    const handleSubmit = (e: React.FormEvent) => {
        e.preventDefault();

        if (id) {
            return api.updateStockTransfer(id, formData).then(
                () => {
                    refresh();
                    toast.success("Stock Transfer Updated Successfully!");
                    onClose();
                    setFormData({ items: [{}] });
                },
                (error) => {
                    setErrorData(error.response.data);
                }
            );
        }

        api.createStockTransfer(formData).then(
            () => {
                refresh();
                toast.success("Stock Created Successfully!");
                onClose();
                setFormData({ items: [{}] });
            },
            (error) => {
                setErrorData(error.response.data);
            }
        );
    };

    const handleSelectChange = (name: any, value: any) => {
        setFormData((prev: any) => ({ ...prev, [name]: value }));
    };

    const onVariantChange = (variantData: any[]) => {
        const updatedVariantData = variantData.map((item) => ({
            ...item,
            variant: id ? item.variant : item.dbId,
            // ordered_quantity: item.quantity,
        }));
        setFormData((prev: any) => ({ ...prev, items: updatedVariantData }));
    };

    
    useEffect(() =>  {
        if (data) {
            const processedData = {
                from_warehouse: data.stockTransfer.fromWarehouse.dbId,
                to_warehouse: data.stockTransfer.toWarehouse.dbId,
                items: data.stockTransfer.items.edges.map((item: any) => ({
                    variant: item.node.variant.dbId,
                    quantity: item.node.quantity,
                    price: item.node.variant.price,
                })),
            };
            setFormData(processedData);

            // set item  separately to avoid infinite loop of re-rendering
            setItems(data.stockTransfer.items.edges.map((item: any) => ({
                    variant: item.node.variant.dbId,
                    quantity: item.node.quantity,
                    price: item.node.variant.price,
                }))
            );
        }
    }, [data])

    


    return (
        <NxtbnDrawer
            isOpen={isOpen}
            onClose={() => {
                onClose();
                setFormData({ items: [{}] });
            }}
            title={id ? 'Edit Stock Transfer' : 'Stock Transfer'}
        >
            <NXForm
                nonFieldError={errorData}
                allButSkipAllLogic={true}
                onSubmit={handleSubmit}
                className="p-6"
                title="Stock Transfer">
               
                <div className="grid grid-cols-1 md:grid-cols-2 gap-6 mb-6">
               
                    <div>
                        <label htmlFor="from_warehouse" className="block text-sm font-medium text-gray-700">
                            From Warehouse
                        </label>
                        <SelectStyled
                            key={data?.stockTransfer?.fromWarehouse?.dbId || 'default-from-warehouse'}
                            name="from_warehouse"
                            errorData={errorData}
                            defaultValue={{ value: data?.stockTransfer?.fromWarehouse?.dbId, label: data?.stockTransfer?.fromWarehouse?.name }}
                            options={makeEnumFriendly(warehouses.results || [])}
                            onChange={(e) => handleSelectChange('from_warehouse', e.value)}
                            className="mt-1"
                        />
                    </div>

                    <div>
                        <label htmlFor="to_warehouse" className="block text-sm font-medium text-gray-700">
                            To Warehouse
                        </label>
                        <SelectStyled
                            key={data?.stockTransfer?.toWarehouse?.dbId || 'default-to-warehouse'}
                            name="to_warehouse"
                            errorData={errorData}
                            defaultValue={{ value: data?.stockTransfer?.toWarehouse?.dbId, label: data?.stockTransfer?.toWarehouse?.name }}
                            options={makeEnumFriendly(warehouses.results || [])}
                            onChange={(e) => handleSelectChange('to_warehouse', e.value)}
                            className="mt-1"
                        />
                    </div>
                </div>

                <div className="mb-6">
                    <ProductWithVariantSearch
                        value={items} usedIn="stock_transfer" onVariantChange={onVariantChange}
                    />
                </div>

                <div className="flex justify-end">
                    <button
                        type="button"
                        onClick={onClose}
                        className="mr-3 px-4 py-2 text-[#0CAF60] border border-[#0CAF60] hover:bg-slate-200 text-sm font-medium rounded-md shadow-sm focus:ring-2 focus:ring-offset-2 focus:ring-gray-400"
                    >
                        Cancel
                    </button>
                    <button
                        type="submit"
                        className="inline-flex justify-center rounded-md border border-transparent px-4 py-2 bg-primary-600 text-base font-medium text-white shadow-sm hover:bg-primary-500 focus:outline-none focus:ring-2 focus:ring-offset-2 sm:text-sm"
                    >
                        Save as Draft
                    </button>
                </div>
            </NXForm>
        </NxtbnDrawer>
    );
}

export default StackTransferForm;
